/** Espelha `InvoiceTributosCalculator` (servidor) para pré-visualização no formulário. */
export type FiscalSettingRates = {
  municipio: string
  company_id?: number | null
  omie_servico_id?: number | null
  omie_conta_corrente_id?: number | null
  omie_servico_label?: string
  omie_conta_label?: string
  irrf: number
  pis: number
  cofins: number
  csll: number
  iss: number
  irrf_withhold: boolean
  pis_withhold: boolean
  cofins_withhold: boolean
  csll_withhold: boolean
  iss_withhold: boolean
}

export function matchFiscalSettingForMunicipio(
  settings: FiscalSettingRates[] | undefined,
  municipio: string,
  companyId?: string | null
) {
  if (!settings?.length) return undefined
  const m = municipio.trim()
  if (!m) return undefined
  const sameMun = settings.filter(
    (s) => s.municipio.trim().localeCompare(m, "pt-BR", { sensitivity: "base" }) === 0
  )
  if (!sameMun.length) return undefined

  const cid = companyId?.trim()
  if (cid) {
    const byCompany = sameMun.filter((s) => s.company_id != null && String(s.company_id) === cid)
    if (byCompany.length) return byCompany[0]
    const legacy = sameMun.filter((s) => s.company_id == null)
    if (legacy.length) return legacy[0]
    return undefined
  }
  return sameMun[0]
}

export type TributoLinhaPreview = {
  codigo: string
  nome: string
  aliquota_percentual: number
  valor: number
  retido: boolean
}

function parcela(valor: number, aliquota: number): number {
  return Math.round(((valor * aliquota) / 100) * 100) / 100
}

/** Espelha `InvoiceTributosCalculator#breakdown` para o formulário. */
export function computeInvoiceTributosBreakdown(
  valor: number,
  s: FiscalSettingRates | undefined
): { linhas: TributoLinhaPreview[]; total: number } {
  if (valor < 0 || !s) {
    return { linhas: [], total: 0 }
  }

  const linhas: TributoLinhaPreview[] = [
    {
      codigo: "irrf",
      nome: "IRRF",
      aliquota_percentual: s.irrf,
      valor: s.irrf_withhold ? parcela(valor, s.irrf) : 0,
      retido: s.irrf_withhold
    },
    {
      codigo: "pis",
      nome: "PIS",
      aliquota_percentual: s.pis,
      valor: s.pis_withhold ? parcela(valor, s.pis) : 0,
      retido: s.pis_withhold
    },
    {
      codigo: "cofins",
      nome: "COFINS",
      aliquota_percentual: s.cofins,
      valor: s.cofins_withhold ? parcela(valor, s.cofins) : 0,
      retido: s.cofins_withhold
    },
    {
      codigo: "csll",
      nome: "CSLL",
      aliquota_percentual: s.csll,
      valor: s.csll_withhold ? parcela(valor, s.csll) : 0,
      retido: s.csll_withhold
    },
    {
      codigo: "iss",
      nome: "ISS",
      aliquota_percentual: s.iss,
      valor: s.iss > 0 ? parcela(valor, s.iss) : 0,
      retido: s.iss_withhold
    }
  ]

  const total = Math.round(linhas.reduce((acc, l) => acc + l.valor, 0) * 100) / 100
  return { linhas, total }
}

export function computeInvoiceTributos(valor: number, s: FiscalSettingRates | undefined): number {
  return computeInvoiceTributosBreakdown(valor, s).total
}
